import { MinusCircleIcon, WarningCircleIcon, WarningIcon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { checklistKey, questionKey, type Conclusion } from '@entities/conclusion';
import { Badge } from '@shared/ui';

import styles from './ConclusionDetails.module.css';

interface ConclusionDetailsProps {
  conclusion: Conclusion;
}

/**
 * Раскрытие заключения до оснований (FE-250, MVP_PRD §38.1–38.2).
 *
 * Четыре блока и одно правило на все: **каждый пункт раскрывается до того, чем
 * он подтверждён**.
 *
 * * **сведения со слов автохауса** — дословными цитатами и с обязательной
 *   пометкой «CarViQ.DE не проверял». Поля без цитаты здесь нет вовсе: она не
 *   украшение, а способ существования этих данных (§6.1);
 * * **что осталось без ответа** — вопросы в состоянии `unanswered` и `refused`.
 *   Отказ показан **данными, а не приговором**: он снижает полноту сведений и
 *   ничего не доказывает о состоянии автомобиля (§36.4);
 * * **расхождения** — обе цитаты рядом, без вывода о причине. Ни «обман», ни
 *   «недостоверно»: модель отвечала на вопрос «противоречат ли эти два
 *   утверждения», а не «почему» (§34.7);
 * * **что проверить лично** — пункты, выведенные из признаков внимания,
 *   неотвеченных вопросов и расхождений. Каждый называет основание: «проверить
 *   лично» без указания, что именно, — это не чек-лист, а тревожная надпись.
 *
 * Пустой блок не рисуется: пустой список «расхождений» читался бы как «мы
 * проверили и всё сходится», а на деле сравнивать было нечего.
 *
 * Редизайн 2026-10: цитаты — нейтральная подложка с линейкой, как в ленте торга;
 * состояние вопроса — бейдж с иконкой той же шкалы, что в блоке проверки
 * (отказ — «компромисс», молчание — «нет данных», DESIGN_SYSTEM §2.4);
 * две цитаты расхождения — две равные колонки (§8).
 */
export function ConclusionDetails({ conclusion }: ConclusionDetailsProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.wrap}>
      {conclusion.seller_statements.length === 0 ? null : (
        <section aria-label={t('conclusion.details.statements')}>
          <h3 className={styles.heading}>{t('conclusion.details.statements')}</h3>
          <ul className={styles.list}>
            {conclusion.seller_statements.map((item) => (
              <li key={item.question_code} className={styles.row}>
                <span className={styles.label}>{t(questionKey(item.question_code))}</span>
                {item.quote === null ? null : (
                  <blockquote className={styles.quote}>{item.quote}</blockquote>
                )}
              </li>
            ))}
          </ul>
          {/* Обязательная оговорка §2: спросили — не значит проверили. */}
          <p className={styles.disclaimer}>{t('conclusion.details.statementsDisclaimer')}</p>
        </section>
      )}

      {conclusion.unanswered.length === 0 ? null : (
        <section aria-label={t('conclusion.details.unanswered')}>
          <h3 className={styles.heading}>{t('conclusion.details.unanswered')}</h3>
          <ul className={styles.list}>
            {conclusion.unanswered.map((item) => (
              <li key={item.question_code} className={styles.row}>
                <span className={styles.label}>{t(questionKey(item.question_code))}</span>
                <span className={styles.status}>
                  <Badge {...statusBadge(item.status)} uppercase={false}>
                    {t(`negotiation.check.${item.status}`)}
                  </Badge>
                </span>
                {item.quote === null ? null : (
                  <blockquote className={styles.quote}>{item.quote}</blockquote>
                )}
              </li>
            ))}
          </ul>
          <p className={styles.disclaimer}>{t('conclusion.details.unansweredDisclaimer')}</p>
        </section>
      )}

      {conclusion.contradictions.length === 0 ? null : (
        <section aria-label={t('conclusion.details.contradictions')}>
          <h3 className={styles.heading}>{t('conclusion.details.contradictions')}</h3>
          <ul className={styles.list}>
            {conclusion.contradictions.map((item) => (
              <li key={`${item.question_code}:${item.code}`} className={styles.row}>
                <span className={styles.label}>{t(questionKey(item.question_code))}</span>
                <div className={styles.quotes}>
                  {item.quotes
                    .filter((quote): quote is string => quote !== null)
                    .map((quote) => (
                      <blockquote key={quote} className={styles.quote}>
                        {quote}
                      </blockquote>
                    ))}
                </div>
              </li>
            ))}
          </ul>
          <p className={styles.disclaimer}>{t('conclusion.details.contradictionsDisclaimer')}</p>
        </section>
      )}

      {conclusion.inspection_checklist.length === 0 ? null : (
        <section aria-label={t('conclusion.details.checklist')}>
          <h3 className={styles.heading}>{t('conclusion.details.checklist')}</h3>
          <ul className={styles.list}>
            {conclusion.inspection_checklist.map((item, index) => (
              <li key={`${item.code}:${index}`} className={styles.row}>
                <span className={styles.label}>{t(checklistKey(item.code), item.params)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/**
 * Тон и иконка состояния вопроса — та же шкала, что в блоке проверки
 * (`CheckAnswers`): отказ — «компромисс», неясный ответ — «требует внимания»,
 * молчание — «нет данных» (DESIGN_SYSTEM §2.4). Отказ не красный: это данные,
 * а не приговор (§36.4).
 */
function statusBadge(status: string): {
  tone: 'compromise' | 'attention' | 'nodata';
  icon: ReactNode;
} {
  if (status === 'refused') return { tone: 'compromise', icon: <WarningIcon weight="bold" /> };
  if (status === 'unclear') return { tone: 'attention', icon: <WarningCircleIcon weight="bold" /> };
  return { tone: 'nodata', icon: <MinusCircleIcon weight="bold" /> };
}
